/**
 * Formatting helpers for Eventbrite-sourced event/course schedule values.
 * SCAPI exposes the `c_eventStartDate` / `c_eventEndDate` custom attributes as
 * ISO-8601 strings (they are typed as `Date` in the SFCC object model).
 */

/** Parses a value into a valid Date, returning null for missing/invalid input. */
function toDate(value: string | Date | undefined | null): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats the event day, e.g. "Mercoledì 26 febbraio" (it-IT).
 *
 * @param start - Event start date (ISO string or Date)
 * @param locale - BCP-47 locale (e.g. 'it-IT')
 * @returns Localized weekday + day + month, or undefined if the date is missing/invalid
 */
export function formatEventDay(start: string | Date | undefined | null, locale: string): string | undefined {
    const date = toDate(start);
    if (!date) return undefined;
    return date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    });
}

/**
 * Formats the event time span, e.g. "20:00 - 23:00" (it-IT).
 * Falls back to just the start time when no end date is available.
 *
 * @param start - Event start date (ISO string or Date)
 * @param end - Event end date (ISO string or Date)
 * @param locale - BCP-47 locale (e.g. 'it-IT')
 * @returns Localized time range, or undefined if the start is missing/invalid
 */
export function formatEventTimeRange(
    start: string | Date | undefined | null,
    end: string | Date | undefined | null,
    locale: string
): string | undefined {
    const startDate = toDate(start);
    if (!startDate) return undefined;

    const timeFormat: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    const startTime = startDate.toLocaleTimeString(locale, timeFormat);

    const endDate = toDate(end);
    if (!endDate) return startTime;

    return `${startTime} - ${endDate.toLocaleTimeString(locale, timeFormat)}`;
}

/**
 * Builds the venue location line from Eventbrite venue attributes, joining the
 * non-empty parts (venue name + city). Returns undefined when nothing is set.
 *
 * @param venueName - `c_eventVenueName`
 * @param venueCity - `c_eventVenueCity`
 * @returns e.g. "Torino Lingotto", or undefined when both parts are empty
 */
export function formatEventLocation(
    venueName: string | undefined | null,
    venueCity: string | undefined | null
): string | undefined {
    const parts = [venueName, venueCity].filter((part): part is string => Boolean(part && part.trim()));
    return parts.length > 0 ? parts.join(' ') : undefined;
}
